/*
 * QBounce revamp behaviours. Purely decorative: scroll progress bar, reveal on scroll,
 * cursor-following glow on cards, ember particles, and pausing decorative animation off screen.
 * Nothing here touches cart, checkout or product logic.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('qb')) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* Scroll-driven effects run only while their element is within a screen of the viewport; when it
     moves further away they run once more, so they settle at their end state, then rest. */
  var nearIO = null;
  function nearGate(el, schedule) {
    var near = true;
    var settled = false;
    if (hasIO) {
      nearIO = nearIO || new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          (entry.target.qbNearCbs || []).forEach(function (cb) {
            cb(entry.isIntersecting);
          });
        });
      }, { rootMargin: '100% 0px' });
      (el.qbNearCbs = el.qbNearCbs || []).push(function (isNear) {
        near = isNear;
        settled = false;
        schedule();
      });
      nearIO.observe(el);
    }
    return function skip() {
      if (near) return false;
      if (settled) return true;
      settled = true;
      return false;
    };
  }

  /* Counters change their number every frame: update the existing text node in place. Replacing it
     (textContent) inserts a new node each frame, which makes the browser recheck the page's :has()
     rules and restyle every container they touch. */
  function setText(el, text) {
    var node = el.firstChild;
    if (node && node.nodeType === 3 && !node.nextSibling) {
      if (node.nodeValue !== text) node.nodeValue = text;
    } else {
      el.textContent = text;
    }
  }

  /* Writes a style only when its value changed: an unchanged write still restyles the element and
     everything inside it on the next frame. */
  function setStyle(el, prop, value) {
    var cache = el.qbStyle || (el.qbStyle = {});
    if (cache[prop] === value) return;
    cache[prop] = value;
    el.style.setProperty(prop, value);
  }

  /* Cards that get the cursor-following glow (styled in qb-revamp.css) */
  var CARD_SELECTOR = [
    '.qb-card',
    '.drawbacks__item',
    '.training-system__card--content',
    '.built-for__item',
    '.steps__item',
    '.real-results__item',
    '.qb-rr__card',
    '.faq-item',
    '.card-wrapper',
    '.multicolumn-card',
    '.qb-final-cta__card',
  ].join(',');

  /* Elements that fade and lift in the first time they scroll into view */
  var REVEAL_SELECTOR = [
    '.drawbacks__heading',
    '.drawbacks__item',
    '.training-system__heading',
    '.training-system__text',
    '.training-system__card',
    '.built-for__heading',
    '.built-for__text',
    '.built-for__item',
    '.built-for__bottom',
    '.steps__heading',
    '.steps__text',
    '.steps__item',
    '.real-results__heading',
    '.real-results__text',
    '.real-results__item',
    '.thirty-days__heading',
    '.thirty-days__text',
    '.compare-table__title',
    '.compare-table',
    '.ugc__title',
    '.faq__title',
    '.faq-item',
    '[data-qb-reveal]',
  ].join(',');

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function initProgress() {
    if (document.querySelector('.qb-progress')) return;
    var bar = document.createElement('div');
    bar.className = 'qb-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    var ticking = false;
    function update() {
      ticking = false;
      var max = root.scrollHeight - window.innerHeight;
      var p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      bar.style.transform = 'scaleX(' + p + ')';
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  function initReveal(scope) {
    if (reduceMotion || !hasIO) return;
    var nodes = (scope || document).querySelectorAll(REVEAL_SELECTOR);
    if (!nodes.length) return;
    var io = initReveal.io || (initReveal.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('qb-in');
        initReveal.io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }));

    var siblings = new Map();
    nodes.forEach(function (el) {
      if (el.classList.contains('qb-reveal') || el.closest('.scroll-trigger')) return;
      // Anything already on screen at load stays put: no flash of hidden content above the fold.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) return;
      // stagger siblings that share a parent
      var index = siblings.get(el.parentNode) || 0;
      siblings.set(el.parentNode, index + 1);
      el.style.setProperty('--qb-delay', Math.min(index, 5) * 0.08 + 's');
      el.classList.add('qb-reveal');
      io.observe(el);
    });
  }

  function initSpotlight() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      var card = e.target && e.target.closest ? e.target.closest(CARD_SELECTOR) : null;
      if (!card) return;
      var rect = card.getBoundingClientRect();
      card.style.setProperty('--mx', e.clientX - rect.left + 'px');
      card.style.setProperty('--my', e.clientY - rect.top + 'px');
    }, { passive: true });
  }

  /* Ember particles: positions derive from the index so they are stable between renders */
  function initEmbers(scope) {
    if (reduceMotion) return;
    (scope || document).querySelectorAll('[data-qb-embers]').forEach(function (host) {
      if (host.childElementCount) return;
      var count = parseInt(host.getAttribute('data-qb-embers'), 10) || 12;
      for (var i = 0; i < count; i++) {
        var size = 2 + ((i * 7) % 5);
        var ember = document.createElement('span');
        ember.className = 'qb-ember';
        ember.style.left = (i * 37) % 100 + '%';
        ember.style.width = size + 'px';
        ember.style.height = size + 'px';
        ember.style.animationDuration = 9 + ((i * 13) % 9) + 's';
        ember.style.animationDelay = -((i * 1.7) % 14) + 's';
        ember.style.setProperty('--drift', ((i % 5) - 2) * 18 + 'px');
        host.appendChild(ember);
      }
    });
  }

  function initOffscreenPause(scope) {
    if (!hasIO) return;
    var io = initOffscreenPause.io || (initOffscreenPause.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) entry.target.removeAttribute('data-qb-offscreen');
        else entry.target.setAttribute('data-qb-offscreen', '');
      });
    }, { rootMargin: '150px 0px' }));
    (scope || document).querySelectorAll('[data-qb-pause]').forEach(function (el) {
      io.observe(el);
    });
    /* every homepage section too: the flag pauses all of a section's looping animations (block ZV) */
    (scope || document).querySelectorAll('main .shopify-section, .shopify-section-group-footer-group').forEach(function (el) {
      io.observe(el);
    });
  }

  /* Rotating ember borders turn on their own layer with a transform (block ZV): animating the conic
     gradient's --qb-angle restyled and repainted the element on every frame. */
  function initRings(scope) {
    (scope || document).querySelectorAll('a.bundle_save_btn, .qb-fc .grid__item.qb-fc__spot').forEach(function (el) {
      if (el.qbRing) return;
      el.qbRing = true;
      var ring = document.createElement('span');
      ring.className = 'qb-ring';
      ring.setAttribute('aria-hidden', 'true');
      ring.appendChild(document.createElement('i'));
      el.appendChild(ring);
      el.classList.add('qb-ring-on');
    });
  }

  /* Decorative videos play only while on screen. Clips rendered by snippets/qb-video.liquid carry
     data-qb-autoplay and preload nothing, so a clip is fetched the first time it comes near the
     viewport. The hero video is paused while scrolled away. With reduced motion, clips keep
     their poster and do not start on their own.
     Videos with data-qb-late (the hero, snippets/qb-hero-video.liquid) wait until the page has
     loaded, or 3s after it is ready, whichever comes first, so they never compete with the first
     screen for the connection. */
  function initVideos(scope) {
    var vids = (scope || document).querySelectorAll('video[data-qb-autoplay], .hero-v2__video-el');
    if (!vids.length) return;
    /* data-qb-poster: the poster is only set once the video is near the screen */
    function poster(v) {
      if (v.dataset.qbPoster && !v.getAttribute('poster')) v.setAttribute('poster', v.dataset.qbPoster);
    }
    function play(v) {
      poster(v);
      if (v.qbUserPaused) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    if (reduceMotion) {
      vids.forEach(function (v) {
        v.removeAttribute('autoplay');
        v.pause();
        poster(v);
      });
      return;
    }
    var io = hasIO && (initVideos.io || (initVideos.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) play(entry.target);
        else entry.target.pause();
      });
    }, { rootMargin: '200px 0px' })));
    function watch(v) {
      if (io) io.observe(v);
      else play(v);
    }
    vids.forEach(function (v) {
      if (!v.hasAttribute('data-qb-late') || document.readyState === 'complete') {
        watch(v);
        return;
      }
      var done = false;
      function go() {
        if (done) return;
        done = true;
        watch(v);
      }
      window.addEventListener('load', go, { once: true });
      setTimeout(go, 3000);
    });
  }

  /* Hero: a small pause/play button for the background video (WCAG 2.2.2) */
  function initHeroVideoToggle(scope) {
    (scope || document).querySelectorAll('[data-qb-video-toggle]').forEach(function (btn) {
      var host = btn.closest('.hero-v2-section');
      var vids = host ? host.querySelectorAll('.hero-v2__video-el') : [];
      if (!vids.length) {
        btn.hidden = true;
        return;
      }
      function sync(paused) {
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
        btn.classList.toggle('is-paused', paused);
      }
      sync(reduceMotion);
      btn.addEventListener('click', function () {
        var paused = btn.getAttribute('aria-pressed') !== 'true';
        vids.forEach(function (v) {
          v.qbUserPaused = paused;
          if (paused) v.pause();
          else {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          }
        });
        sync(paused);
      });
    });
  }

  /* Train in 4 Simple Steps, timeline layout (as on the QBounce app landing page).
     Desktop (990px+): --p (0..1) fills the rail as the steps pass the middle of the screen; the
     step at the middle is active and the sticky picture shows its image.
     Phones/tablets: the steps are a swipeable row; the dots follow the card in view. */
  function initStepsTimeline(scope) {
    (scope || document).querySelectorAll('[data-qb-stl]').forEach(function (wrap) {
      if (wrap.qbStl) return;
      wrap.qbStl = true;
      /* steps (Train in 4 Simple Steps) or athletes (QBounce products are trusted by) */
      var items = Array.prototype.slice.call(wrap.querySelectorAll('.steps__item, [data-qb-stl-item]'));
      var imgs = Array.prototype.slice.call(wrap.querySelectorAll('[data-qb-stl-img]'));
      var section = wrap.closest('.steps-section, .how-to-use') || wrap.parentNode;
      var dots = Array.prototype.slice.call(wrap.querySelectorAll('.qb-stl__dots i'));
      var cardDots = Array.prototype.slice.call(section.querySelectorAll('.qb-stl__dots--cards i'));
      if (!items.length) return;
      var desktop = window.matchMedia('(min-width: 990px)');
      var active = -1;
      var ticking = false;
      var skip = nearGate(wrap, onScroll);

      function setActive(n) {
        if (n === active) return;
        active = n;
        items.forEach(function (it, i) {
          it.classList.toggle('is-active', i === n);
          it.classList.toggle('is-past', i < n);
        });
        imgs.forEach(function (im) {
          im.classList.toggle('is-active', Number(im.getAttribute('data-qb-stl-img')) === n);
        });
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === n);
        });
      }

      function update() {
        ticking = false;
        if (skip()) return;
        if (desktop.matches) {
          var r = wrap.getBoundingClientRect();
          var mid = window.innerHeight * 0.5;
          var p = (mid - r.top) / (r.height || 1);
          p = Math.min(1, Math.max(0, p));
          setStyle(wrap, '--p', p.toFixed(4));
          setActive(Math.min(items.length - 1, Math.floor(p * items.length)));
        } else {
          /* the card nearest the row's left edge is the current one */
          var left = wrap.getBoundingClientRect().left;
          var best = 0;
          var bestDist = Infinity;
          items.forEach(function (it, i) {
            var d = Math.abs(it.getBoundingClientRect().left - left);
            if (d < bestDist) {
              bestDist = d;
              best = i;
            }
          });
          /* the last cards cannot reach the left edge: scrolled to the end means the last card */
          if (wrap.scrollLeft > 0 && wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 2) best = items.length - 1;
          cardDots.forEach(function (d, i) {
            d.classList.toggle('is-active', i === best);
          });
        }
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      wrap.addEventListener('scroll', onScroll, { passive: true });
      update();
    });
  }

  /* One Complete Training System, living-room setup: the ball bounces from zone to zone on the
     drawn mat; each landing lights that zone and the matching checklist point. Runs only while
     the scene is on screen; stops for good once the visitor picks a point or a zone. With
     reduced motion nothing moves on its own (picking still works). */
  function initRoom(scope) {
    (scope || document).querySelectorAll('[data-qb-room]').forEach(function (room) {
      if (room.qbRoom) return;
      room.qbRoom = true;
      var floor = room.querySelector('.qb-room__floor');
      var ball = room.querySelector('.qb-room__ball');
      var hop = room.querySelector('.qb-room__ball-hop');
      var points = Array.prototype.slice.call(room.querySelectorAll('[data-qb-point]'));
      var zones = Array.prototype.slice.call(room.querySelectorAll('.qb-room__zone'));
      if (!floor || !ball || !points.length) return;
      var current = -1;
      var timer = null;
      var auto = !reduceMotion;
      var onScreen = false;

      function place(zone, instant) {
        var z = room.querySelector('.qb-room__zone--' + zone);
        if (!z) return;
        var zr = z.getBoundingClientRect();
        var fr = floor.getBoundingClientRect();
        var x = zr.left + zr.width / 2 - fr.left;
        var y = zr.top + zr.height / 2 - fr.top;
        if (instant) ball.style.transition = 'none';
        ball.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
        if (instant) {
          void ball.offsetWidth;
          ball.style.transition = '';
        }
      }

      function go(n, instant) {
        current = (n + points.length) % points.length;
        var zone = points[current].getAttribute('data-qb-zone');
        points.forEach(function (p, k) {
          p.setAttribute('aria-pressed', k === current ? 'true' : 'false');
          p.classList.toggle('is-on', k === current);
        });
        zones.forEach(function (el) {
          el.classList.toggle('is-lit', el.getAttribute('data-qb-zone') === zone);
        });
        place(zone, instant);
        if (!instant && !reduceMotion && hop) {
          hop.classList.remove('is-hop');
          void hop.offsetWidth;
          hop.classList.add('is-hop');
        }
      }

      function start() {
        if (!auto || timer || !onScreen) return;
        timer = setInterval(function () {
          /* build-your-kit: the ball only travels while both the mat and the ball are in the kit */
          if (room.classList.contains('kit-no-mat') || room.classList.contains('kit-no-ball')) return;
          go(current + 1);
        }, 2400);
      }

      function stop() {
        clearInterval(timer);
        timer = null;
      }

      function pick(n) {
        auto = false;
        stop();
        go(n);
      }

      if (hop) {
        hop.addEventListener('animationend', function (e) {
          if (e.animationName === 'qb-room-hop') hop.classList.remove('is-hop');
        });
      }

      points.forEach(function (p, k) {
        p.addEventListener('click', function () {
          pick(k);
        });
      });

      function nextPointFor(zone) {
        for (var step = 1; step <= points.length; step++) {
          var k = (current + step) % points.length;
          if (points[k].getAttribute('data-qb-zone') === zone) return k;
        }
        return current;
      }

      /* "Try a drill": the screen calls a zone, the visitor taps it; three in a row */
      var drill = room.querySelector('[data-qb-drill]');
      var playing = false;
      var target = null;
      var hits = 0;
      var started = 0;
      var section = room.closest('section') || room;
      var msg = drill && drill.querySelector('.qb-drill__msg');
      var num = drill && drill.querySelector('.qb-drill__num');
      var startBtn = drill && drill.querySelector('.qb-drill__start');
      var dots = drill ? drill.querySelectorAll('.qb-drill__dots i') : [];

      function call() {
        var options = ['1', '2', '3', '4'].filter(function (z) {
          return z !== target && z !== (current >= 0 ? points[current].getAttribute('data-qb-zone') : '');
        });
        target = options[Math.floor(Math.random() * options.length)];
        zones.forEach(function (el) {
          el.classList.toggle('is-called', el.getAttribute('data-qb-zone') === target);
        });
        num.textContent = target;
        num.classList.remove('is-pop');
        void num.offsetWidth;
        num.classList.add('is-pop');
        msg.textContent = 'Tap zone ' + target + '!';
      }

      function setDots() {
        Array.prototype.forEach.call(dots, function (d, k) {
          d.classList.toggle('is-on', k < hits);
        });
      }

      function startDrill() {
        auto = false;
        stop();
        playing = true;
        hits = 0;
        target = null;
        started = Date.now();
        setDots();
        drill.setAttribute('data-state', 'playing');
        section.classList.remove('qb-drill-done');
        points.forEach(function (p) {
          p.classList.remove('is-done');
        });
        call();
        var first = room.querySelector('.qb-room__zone--' + target);
        if (first && document.activeElement === startBtn) startBtn.blur();
      }

      function finish() {
        playing = false;
        target = null;
        zones.forEach(function (el) {
          el.classList.remove('is-called');
        });
        var secs = ((Date.now() - started) / 1000).toFixed(1);
        drill.setAttribute('data-state', 'done');
        num.textContent = '';
        msg.textContent = 'Three in ' + secs + 's. That is how every session works.';
        startBtn.textContent = 'Play again';
        /* every point lights up in turn, then the button below glows once */
        points.forEach(function (p, k) {
          setTimeout(function () {
            p.classList.add('is-done');
          }, reduceMotion ? 0 : 110 * k);
        });
        setTimeout(function () {
          section.classList.add('qb-drill-done');
        }, reduceMotion ? 0 : 110 * points.length);
      }

      function hit(zone) {
        if (zone === target) {
          go(nextPointFor(zone));
          hits++;
          setDots();
          if (hits >= 3) {
            finish();
          } else {
            msg.textContent = 'Nice!';
            target = null;
            zones.forEach(function (el) {
              el.classList.remove('is-called');
            });
            setTimeout(call, reduceMotion ? 150 : 650);
          }
        } else if (target) {
          var z = room.querySelector('.qb-room__zone--' + zone);
          if (z) {
            z.classList.remove('is-miss');
            void z.offsetWidth;
            z.classList.add('is-miss');
          }
          msg.textContent = 'Not that one. Tap zone ' + target + '.';
        }
      }

      if (drill && startBtn) {
        startBtn.addEventListener('click', startDrill);
        document.addEventListener('keydown', function (e) {
          if (!playing || !/^[1-4]$/.test(e.key)) return;
          var r = room.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight) return;
          hit(e.key);
        });
      }

      /* a zone tap: in the drill it is an answer; otherwise it goes to the next point of that zone */
      zones.forEach(function (z) {
        z.addEventListener('click', function () {
          var zone = z.getAttribute('data-qb-zone');
          if (playing) {
            hit(zone);
            return;
          }
          pick(nextPointFor(zone));
        });
      });

      requestAnimationFrame(function () {
        go(0, true);
      });
      window.addEventListener('resize', function () {
        if (current >= 0) place(points[current].getAttribute('data-qb-zone'), true);
      });

      if (hasIO) {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen) {
            if (current >= 0) place(points[current].getAttribute('data-qb-zone'), true);
            start();
          } else {
            stop();
          }
        }, { threshold: 0.25 }).observe(room);
      } else {
        onScreen = true;
        start();
      }
    });
  }

  /* One Complete Training System, "Build your kit": three toggles add or remove the app (screen),
     the mat and the ball from the scene; all three shows the bundle. Without JavaScript all
     parts show. The first time the scene comes into view the kit assembles itself quickly, part
     by part. A part in the kit stays in: toggles only add. Reduced motion: everything starts in. */
  function initKit(scope) {
    (scope || document).querySelectorAll('[data-qb-kit]').forEach(function (room) {
      if (room.qbKit) return;
      room.qbKit = true;
      var toggles = Array.prototype.slice.call(room.querySelectorAll('[data-qb-kit-toggle]'));
      var count = room.querySelector('[data-qb-kit-count]');
      var screenVideo = room.querySelector('.qb-room__screen video');
      var clipVideo = room.querySelector('.qb-room__clip video');
      var state = { app: true, mat: true, ball: true };
      var timers = [];

      function setVideo(v, on) {
        if (!v) return;
        v.qbUserPaused = !on;
        if (on) {
          if (!reduceMotion) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          }
        } else {
          v.pause();
        }
      }

      function apply() {
        var n = 0;
        ['app', 'mat', 'ball'].forEach(function (part) {
          room.classList.toggle('kit-no-' + part, !state[part]);
          if (state[part]) n++;
        });
        room.classList.toggle('kit-complete', n === 3);
        toggles.forEach(function (t) {
          var on = state[t.getAttribute('data-qb-kit-toggle')];
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
          /* a part in the kit stays in the kit */
          if (on) t.setAttribute('aria-disabled', 'true');
          else t.removeAttribute('aria-disabled');
        });
        if (count) count.textContent = n;
        setVideo(screenVideo, state.app);
        setVideo(clipVideo, state.mat);
      }

      toggles.forEach(function (t) {
        t.addEventListener('click', function () {
          var part = t.getAttribute('data-qb-kit-toggle');
          if (state[part]) {
            /* already in the kit: a small bounce instead of removing it */
            t.classList.remove('is-nudge');
            void t.offsetWidth;
            t.classList.add('is-nudge');
            return;
          }
          /* tapping before or during the self-assembly just adds that part sooner */
          state[part] = true;
          apply();
        });
      });

      if (reduceMotion || !hasIO) {
        apply();
        return;
      }

      /* start empty (before it is seen, so nothing disappears in view), then add the app,
         the mat and the ball one after another once the scene is on screen */
      state = { app: false, mat: false, ball: false };
      apply();
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        room.classList.add('kit-assembling');
        ['app', 'mat', 'ball'].forEach(function (part, i) {
          timers.push(setTimeout(function () {
            state[part] = true;
            apply();
            if (i === 2) {
              room.classList.remove('kit-assembling');
              timers = [];
            }
          }, 150 + i * 350));
        });
      }, { threshold: 0, rootMargin: '0px 0px -15% 0px' });
      io.observe(room);
    });
  }

  /* Announcement line: keep the same reading speed whatever the text length, by setting the
     loop duration from the width of one copy (the CSS moves the track by exactly one copy). */
  function initMarquee(scope) {
    (scope || document).querySelectorAll('[data-qb-marquee]').forEach(function (bar) {
      var group = bar.querySelector('.qb-marquee__group');
      if (!group) return;
      function set() {
        var w = group.getBoundingClientRect().width;
        if (w) bar.style.setProperty('--qb-mq-dur', (w / 55).toFixed(1) + 's');
      }
      set();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(set);
    });
  }

  /* Footer menus fold into tap-to-open groups on phones. The heading becomes the toggle;
     on wider screens everything stays open and the heading is plain text again. */
  function initFooterFold(scope) {
    var blocks = (scope || document).querySelectorAll('.footer-block--menu');
    if (!blocks.length) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var items = [];
    blocks.forEach(function (block, i) {
      var head = block.querySelector('.footer-block__heading');
      var list = block.querySelector('.footer-block__details-content');
      if (!head || !list || block.qbFold) return;
      block.qbFold = true;
      if (!list.id) list.id = 'qb-footer-menu-' + i;
      function toggle() {
        if (!mq.matches) return;
        var open = !block.classList.contains('is-open');
        block.classList.toggle('is-open', open);
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      head.addEventListener('click', toggle);
      head.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
      items.push({ block: block, head: head, list: list });
    });
    function apply() {
      items.forEach(function (it) {
        if (mq.matches) {
          it.block.classList.add('qb-fold');
          it.head.setAttribute('role', 'button');
          it.head.setAttribute('tabindex', '0');
          it.head.setAttribute('aria-controls', it.list.id);
          it.head.setAttribute('aria-expanded', it.block.classList.contains('is-open') ? 'true' : 'false');
        } else {
          it.block.classList.remove('qb-fold');
          ['role', 'tabindex', 'aria-controls', 'aria-expanded'].forEach(function (a) {
            it.head.removeAttribute(a);
          });
        }
      });
    }
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  /* Scroll-linked parallax for the rising basketball in the closing section */
  function initRise() {
    var nodes = document.querySelectorAll('[data-qb-rise]');
    if (!nodes.length || reduceMotion) return;
    var ticking = false;
    var gates = [];
    nodes.forEach(function (el, i) {
      gates[i] = nearGate(el.closest('[data-qb-pause]') || el.parentNode, schedule);
    });
    function schedule() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }
    function update() {
      ticking = false;
      nodes.forEach(function (el, i) {
        if (gates[i]()) return;
        var host = el.closest('[data-qb-pause]') || el.parentNode;
        var rect = host.getBoundingClientRect();
        var vh = window.innerHeight;
        // 0 when the section's top enters the viewport, 1 when its bottom reaches the viewport bottom
        var p = (vh - rect.top) / (rect.height || 1);
        p = Math.min(1, Math.max(0, p));
        setStyle(el, 'transform', 'translate3d(-50%, ' + (1 - p) * 220 + 'px, 0) rotate(' + (p - 1) * 40 + 'deg)');
      });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  var BALL_SVG =
    '<svg viewBox="0 0 200 200" class="qb-ball" aria-hidden="true" focusable="false">' +
    '<defs><radialGradient id="qb-cue-fill" cx="34%" cy="30%" r="75%"><stop offset="0%" stop-color="#FFB35C"/>' +
    '<stop offset="38%" stop-color="#E8700F"/><stop offset="78%" stop-color="#9C4300"/><stop offset="100%" stop-color="#4A1E00"/>' +
    '</radialGradient><clipPath id="qb-cue-clip"><circle cx="100" cy="100" r="98"/></clipPath></defs>' +
    '<circle cx="100" cy="100" r="98" fill="url(#qb-cue-fill)"/>' +
    '<g clip-path="url(#qb-cue-clip)" fill="none" stroke="#1d0b00" stroke-width="8" stroke-linecap="round">' +
    '<path d="M0 96 Q100 116 200 96"/><path d="M104 0 Q86 100 104 200"/><path d="M34 14 Q78 100 30 188"/>' +
    '<path d="M166 12 Q124 100 172 186"/></g></svg>';

  /* Hero: rising embers and a small dribbling-ball scroll cue. Decorative only, added without touching the section markup. */
  function decorateHero(scope) {
    (scope || document).querySelectorAll('.hero-v2-section').forEach(function (hero) {
      if (hero.querySelector('.qb-hero-decor')) return;
      var decor = document.createElement('div');
      decor.className = 'qb-hero-decor';
      decor.setAttribute('aria-hidden', 'true');
      decor.innerHTML =
        '<div class="qb-embers" data-qb-embers="12"></div>' +
        '<div class="qb-hero-cue"><div class="qb-dribble">' + BALL_SVG + '</div><div class="qb-dribble-shadow"></div></div>';
      hero.appendChild(decor);
      hero.setAttribute('data-qb-pause', '');
    });
  }

  /* Announcement bar: show one message at a time, swapping every four bounces of the ball */
  function initAnnBar(scope) {
    (scope || document).querySelectorAll('[data-qb-annbar]').forEach(function (bar) {
      if (bar.qbAnnBar) return;
      var msgs = Array.prototype.slice.call(bar.querySelectorAll('.qb-annbar__msg'));
      if (msgs.length < 2) return;
      var index = Math.max(0, msgs.findIndex(function (m) { return m.classList.contains('is-active'); }));
      var paused = false;

      function show(next) {
        index = (next + msgs.length) % msgs.length;
        msgs.forEach(function (m, i) {
          var active = i === index;
          m.classList.toggle('is-active', active);
          if (active) m.removeAttribute('aria-hidden');
          else m.setAttribute('aria-hidden', 'true');
          var link = m.querySelector('a');
          if (link) link.tabIndex = active ? 0 : -1;
        });
      }

      bar.addEventListener('mouseenter', function () { paused = true; });
      bar.addEventListener('mouseleave', function () { paused = false; });
      bar.addEventListener('focusin', function () { paused = true; });
      bar.addEventListener('focusout', function () { paused = false; });
      bar.qbAnnBar = { show: show, msgs: msgs, pause: function (v) { paused = v; } };
      window.setInterval(function () {
        if (!paused && !document.hidden) show(index + 1);
      }, 3600);
    });
  }

  /* Hero: the framed video leans a few degrees toward the pointer (desktop, mouse only) */
  function initTilt(scope) {
    if (reduceMotion || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    (scope || document).querySelectorAll('[data-qb-tilt]').forEach(function (frame) {
      if (frame.qbTilt) return;
      frame.qbTilt = true;
      var host = frame.closest('.hero-v2-section') || frame;
      host.addEventListener('pointermove', function (e) {
        var rect = frame.getBoundingClientRect();
        var x = Math.max(-1, Math.min(1, (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2)));
        var y = Math.max(-1, Math.min(1, (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2)));
        frame.style.setProperty('--qb-ry', (x * 6).toFixed(2) + 'deg');
        frame.style.setProperty('--qb-rx', (-y * 5).toFixed(2) + 'deg');
      }, { passive: true });
      host.addEventListener('pointerleave', function () {
        frame.style.setProperty('--qb-ry', '0deg');
        frame.style.setProperty('--qb-rx', '0deg');
      });
    });
  }

  /* Hero: the big basketball lifts and turns a little as the hero scrolls away */
  function initMoon() {
    var moons = document.querySelectorAll('[data-qb-moon]');
    if (!moons.length || reduceMotion) return;
    var ticking = false;
    var gates = [];
    moons.forEach(function (moon, i) {
      var host = moon.closest('.hero-v2-section');
      gates[i] = host ? nearGate(host, schedule) : function () { return true; };
    });
    function schedule() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }
    function update() {
      ticking = false;
      moons.forEach(function (moon, i) {
        var host = moon.closest('.hero-v2-section');
        if (!host || gates[i]()) return;
        var p = Math.min(1, Math.max(0, window.scrollY / (host.offsetHeight || 1)));
        setStyle(moon, 'transform', 'translate3d(-50%, ' + (-p * 160).toFixed(1) + 'px, 0) rotate(' + (p * 25).toFixed(1) + 'deg)');
      });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    update();
  }

  /* "Problems, then the answer": a line that fills as the section scrolls through the viewport */
  function initFlow() {
    var flows = document.querySelectorAll('[data-qb-flow]');
    if (!flows.length) return;
    if (reduceMotion) {
      flows.forEach(function (flow) {
        flow.style.setProperty('--p', '1');
        var host = flow.closest('section');
        if (host) {
          host.classList.add('qb-reached');
          host.querySelectorAll('.qb-db__cards > .drawbacks__item').forEach(function (card) {
            card.classList.add('qb-lit');
          });
        }
      });
      return;
    }
    // the heading of the section right after the line is "the answer": it waits for the line
    var answers = [];
    flows.forEach(function (flow, i) {
      var host = flow.closest('section');
      var wrap = host && host.closest('.shopify-section');
      var next = wrap && wrap.nextElementSibling;
      var head = next && next.querySelector('.qb-ts .training-system__heading');
      if (!head) return;
      next.classList.add('qb-answer');
      answers[i] = { section: next, head: head, done: false };
    });
    var ticking = false;
    var gates = [];
    flows.forEach(function (flow, i) {
      gates[i] = nearGate(flow, schedule);
    });
    function schedule() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }
    function update() {
      ticking = false;
      var vh = window.innerHeight;
      flows.forEach(function (flow, flowIndex) {
        if (gates[flowIndex]()) return;
        var rect = flow.getBoundingClientRect();
        // starts as the lines enter the lower part of the screen, completes around mid-screen
        var p = (vh * 0.88 - rect.top) / (rect.height + vh * 0.26);
        p = Math.min(1, Math.max(0, p));
        setStyle(flow, '--p', p.toFixed(3));
        var host = flow.closest('section');
        if (!host) return;
        host.classList.toggle('qb-reached', p >= 1);
        // once the line has arrived and the heading is on screen: the image bounces, the heading lands
        var answer = answers[flowIndex];
        if (answer && !answer.done && p >= 1 && answer.head.getBoundingClientRect().top < vh * 0.9) {
          answer.done = true;
          host.classList.add('qb-bounced');
          answer.section.classList.add('qb-answer-in');
        }
        // cards light one after another, left to right, finishing just as the line starts to fill
        var cards = host.querySelectorAll('.qb-db__cards > .drawbacks__item');
        if (!cards.length) return;
        var rects = [];
        var minLeft = Infinity;
        var maxLeft = -Infinity;
        cards.forEach(function (card) {
          var r = card.getBoundingClientRect();
          rects.push(r);
          minLeft = Math.min(minLeft, r.left);
          maxLeft = Math.max(maxLeft, r.left);
        });
        var span = maxLeft - minLeft;
        cards.forEach(function (card, i) {
          var lead = span > 0 ? ((maxLeft - rects[i].left) / span) * vh * 0.22 : 0;
          card.classList.toggle('qb-lit', rects[i].bottom < vh * 0.88 + lead);
        });
      });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* "Two become one": the two cards slide together as their plus scrolls up the screen */
  function initJoin() {
    var groups = document.querySelectorAll('[data-qb-join]');
    if (!groups.length || reduceMotion) return;
    var items = [];
    groups.forEach(function (group) {
      var host = group.closest('section');
      if (!host) return;
      host.classList.add('qb-join-live');
      items.push({ el: group, host: host, mark: group.querySelector('.training-system__divider') || group });
    });
    var ticking = false;
    items.forEach(function (item) {
      item.skip = nearGate(item.el, schedule);
    });
    function schedule() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }
    function update() {
      ticking = false;
      var vh = window.innerHeight;
      items.forEach(function (item) {
        if (item.skip()) return;
        var rect = item.mark.getBoundingClientRect();
        var centre = rect.top + rect.height / 2;
        // starts as the plus enters at the bottom of the screen, joined a little below mid-screen
        var p = (vh * 0.95 - centre) / (vh * 0.4);
        p = Math.min(1, Math.max(0, p));
        p = p * p * (3 - 2 * p);
        setStyle(item.el, '--j', p.toFixed(3));
        var joined = p >= 0.999;
        item.host.classList.toggle('qb-joined', joined);
        if (joined) item.host.classList.add('qb-join-done');
      });
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* "Trusted by" card deck: the cards start stacked and slanted at the centre of the first row and
     deal out to their grid places as the section scrolls up the screen (eased --p per card, left to
     right); scrolling back folds them into the deck again. Reduced motion: the cards just sit in place. */
  function initDeck(scope) {
    if (reduceMotion) return;
    (scope || document).querySelectorAll('[data-qb-deck]').forEach(function (grid) {
      if (grid.qbDeck) return;
      grid.qbDeck = true;
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.qb-deck__card'));
      var n = cards.length;
      if (!n) return;
      var TILT = [-4, 3, -7, 6, -2, 8, -5, 4];
      var ticking = false;
      var skip = nearGate(grid, onScroll);

      function measure() {
        /* offsets ignore transforms, so these are the settled places */
        var first = cards[0];
        var ax = grid.clientWidth / 2;
        var ay = first.offsetTop + first.offsetHeight / 2;
        cards.forEach(function (c, i) {
          var k = i - (n - 1) / 2;
          c.style.setProperty('--dx', (ax - (c.offsetLeft + c.offsetWidth / 2) + k * 4).toFixed(1) + 'px');
          c.style.setProperty('--dy', (ay - (c.offsetTop + c.offsetHeight / 2) + i * 3).toFixed(1) + 'px');
          c.style.setProperty('--r', TILT[i % TILT.length] + 'deg');
          c.style.zIndex = String(n - i);
        });
      }

      function update() {
        ticking = false;
        if (skip()) return;
        var vh = window.innerHeight;
        var r = grid.getBoundingClientRect();
        /* starts once the folded deck is in view; fully dealt when the row is a third of the way down
           the screen, or (taller grids on phones) when the grid's bottom reaches the bottom of the screen */
        var start = vh * 0.72;
        var end = Math.min(vh * 0.32, vh * 0.95 - r.height);
        var p = Math.min(1, Math.max(0, (start - r.top) / (start - end)));
        var span = 0.45;
        cards.forEach(function (c, i) {
          var q = (p - (span * i) / Math.max(1, n - 1)) / (1 - span);
          q = Math.min(1, Math.max(0, q));
          q = 1 - Math.pow(1 - q, 3);
          setStyle(c, '--p', q.toFixed(4));
        });
        grid.classList.toggle('is-dealt', p >= 0.98);
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      function remeasure() {
        measure();
        onScroll();
      }

      measure();
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', remeasure);
      window.addEventListener('load', remeasure);
      if (window.ResizeObserver) new ResizeObserver(remeasure).observe(grid);
    });
  }

  /* Real Results, live result cards: each number counts up from 0 the first time its card is on
     screen (on phones, when it is swiped into view), and the bar picture beside it fills along
     with it (--m). Phones: the dot under the row follows the swiped card. With reduced motion or
     without IntersectionObserver the numbers simply show their final value. */
  function initResults(scope) {
    (scope || document).querySelectorAll('[data-qb-rr]').forEach(function (grid) {
      if (grid.qbRr) return;
      grid.qbRr = true;
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.qb-rr__card'));
      var dots = Array.prototype.slice.call((grid.parentNode || grid).querySelectorAll('.qb-rr__dots i'));

      if (!reduceMotion && hasIO) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            run(entry.target);
          });
        }, { threshold: 0.45 });

        cards.forEach(function (card) {
          var num = card.querySelector('[data-qb-count]');
          var match = num && num.textContent.trim().match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
          if (!match) return;
          card.qbCount = { el: num, pre: match[1], to: parseFloat(match[2]), dec: (match[2].split('.')[1] || '').length, post: match[3] };
          card.qbMeter = card.querySelector('.qb-rr__meter');
          set(card, 0);
          io.observe(card);
        });
      }

      function set(card, q) {
        var c = card.qbCount;
        setText(c.el, c.pre + (c.to * q).toFixed(c.dec) + c.post);
        if (card.qbMeter) setStyle(card.qbMeter, '--m', q.toFixed(4));
      }

      function run(card) {
        var start = null;
        var duration = 1100;
        function step(t) {
          if (start === null) start = t;
          var p = Math.min(1, (t - start) / duration);
          set(card, 1 - Math.pow(1 - p, 3));
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }

      if (!dots.length) return;
      var ticking = false;
      function update() {
        ticking = false;
        var left = grid.getBoundingClientRect().left;
        var best = 0;
        var bestDist = Infinity;
        cards.forEach(function (card, i) {
          var d = Math.abs(card.getBoundingClientRect().left - left);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        if (grid.scrollLeft > 0 && grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2) best = cards.length - 1;
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === best);
        });
      }
      grid.addEventListener('scroll', function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* What Happens in 30 Days, progress story: picking a milestone (thumbnail, arrow, swipe on the
     photo, arrow keys on the thumbnails) swaps the photo and text, counts the "Day N" number to
     the milestone's day and lights the month's cells one after another up to it. The active
     thumbnail's bar is a CSS animation that only runs while the panel is on screen (.is-live)
     and not hovered; when it ends the next milestone plays. With reduced motion the bar does
     not run, so nothing changes on its own. */
  function initDayStory(scope) {
    (scope || document).querySelectorAll('[data-qb-ds]').forEach(function (root) {
      if (root.qbDs) return;
      root.qbDs = true;
      var steps = Array.prototype.slice.call(root.querySelectorAll('[data-qb-ds-step]'));
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-qb-ds-tab]'));
      var imgs = Array.prototype.slice.call(root.querySelectorAll('[data-qb-ds-img]'));
      var cells = Array.prototype.slice.call(root.querySelectorAll('.qb-ds__cal i'));
      var dayEl = root.querySelector('[data-qb-ds-day]');
      var photo = root.querySelector('[data-qb-ds-photo]');
      var panel = root.querySelector('.qb-ds__panel') || root;
      var n = steps.length;
      if (!n) return;
      var active = 0;
      var day = dayOf(0);
      var countRaf = null;

      function dayOf(i) {
        return Math.min(30, Math.max(1, parseInt(steps[i].getAttribute('data-qb-ds-step'), 10) || 1));
      }

      function setDay(target) {
        var from = day;
        day = target;
        var forward = !reduceMotion && target > from;
        cells.forEach(function (c, i) {
          var d = i + 1;
          c.style.transitionDelay = forward && d > from && d <= target ? ((d - from - 1) * 0.022).toFixed(3) + 's' : '0s';
          c.classList.toggle('is-on', d <= target);
          c.classList.toggle('is-now', d === target);
        });
        if (!dayEl) return;
        if (countRaf) cancelAnimationFrame(countRaf);
        if (reduceMotion || from === target) {
          dayEl.textContent = target;
          return;
        }
        var duration = Math.max(250, Math.abs(target - from) * 22);
        var start = null;
        function step(t) {
          if (start === null) start = t;
          var p = Math.min(1, (t - start) / duration);
          setText(dayEl, String(Math.round(from + (target - from) * p)));
          countRaf = p < 1 ? requestAnimationFrame(step) : null;
        }
        countRaf = requestAnimationFrame(step);
      }

      function show(i) {
        i = (i + n) % n;
        if (i === active) return;
        active = i;
        steps.forEach(function (s, k) {
          s.classList.toggle('is-active', k === i);
        });
        tabs.forEach(function (t, k) {
          t.classList.toggle('is-active', k === i);
          t.classList.toggle('is-past', k < i);
          t.setAttribute('aria-selected', k === i ? 'true' : 'false');
          t.tabIndex = k === i ? 0 : -1;
        });
        imgs.forEach(function (im) {
          im.classList.toggle('is-active', Number(im.getAttribute('data-qb-ds-img')) === i);
        });
        setDay(dayOf(i));
      }

      tabs.forEach(function (t, k) {
        t.tabIndex = k === 0 ? 0 : -1;
        t.addEventListener('click', function () {
          show(k);
        });
        t.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          show(active + dir);
          tabs[active].focus();
        });
      });
      var prev = root.querySelector('[data-qb-ds-prev]');
      var next = root.querySelector('[data-qb-ds-next]');
      if (prev) prev.addEventListener('click', function () { show(active - 1); });
      if (next) next.addEventListener('click', function () { show(active + 1); });

      /* the story bar of the active thumbnail ran out: play the next milestone */
      root.addEventListener('animationend', function (e) {
        if (e.animationName !== 'qb-ds-bar' && e.animationName !== 'qb-ds-bar-scale') return;
        if (e.target.closest('[data-qb-ds-tab]') !== tabs[active]) return;
        show(active + 1);
      });

      if (hasIO) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            root.classList.toggle('is-live', entry.isIntersecting);
          });
        }, { threshold: 0.35 }).observe(panel);
      }

      /* swipe the photo on touch screens */
      if (photo) {
        var x0 = null;
        var y0 = null;
        photo.addEventListener('touchstart', function (e) {
          x0 = e.touches[0].clientX;
          y0 = e.touches[0].clientY;
        }, { passive: true });
        photo.addEventListener('touchend', function (e) {
          if (x0 === null) return;
          var dx = e.changedTouches[0].clientX - x0;
          var dy = e.changedTouches[0].clientY - y0;
          x0 = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(active + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }
    });
  }

  /* Hear From Happy Users, moving review wall: the row of cards drifts to the left by moving its
     scroll position, and copies of the cards follow the originals so it loops without an end.
     It is a normal scroll row, so swiping, dragging (mouse), the arrows and the keyboard all work;
     any of those, hovering, focus inside, the pause button or a video playing with sound hold it
     still (user moves hold it for a moment, then it drifts on). It only runs while on screen and
     never moves on its own with reduced motion. Copies are hidden from assistive tech. */
  function initReviewWall(scope) {
    (scope || document).querySelectorAll('[data-qb-rv]').forEach(function (wall) {
      if (wall.qbRv) return;
      wall.qbRv = true;
      var vp = wall.querySelector('[data-qb-rv-viewport]');
      var row = wall.querySelector('[data-qb-rv-row]');
      if (!vp || !row) return;
      var originals = Array.prototype.slice.call(row.children);
      if (!originals.length) return;
      var pauseBtn = wall.querySelector('[data-qb-rv-pause]');
      var setWidth = 0;
      var pos = 0;
      var last = 0;
      var holdUntil = 0;
      var onScreen = false;
      var hovered = false;
      var focused = false;
      var userPaused = false;
      var soundVideo = null;
      var raf = null;
      var speed = window.matchMedia('(max-width: 749px)').matches ? 26 : 34;

      function makeCopies() {
        row.querySelectorAll('[data-qb-rv-copy]').forEach(function (c) {
          c.remove();
        });
        function addSet() {
          var frag = document.createDocumentFragment();
          originals.forEach(function (card) {
            var c = card.cloneNode(true);
            c.setAttribute('data-qb-rv-copy', '');
            c.setAttribute('aria-hidden', 'true');
            c.removeAttribute('data-shopify-editor-block');
            c.querySelectorAll('button, a, [tabindex]').forEach(function (el) {
              el.setAttribute('tabindex', '-1');
            });
            c.querySelectorAll('video').forEach(function (v) {
              v.muted = true;
            });
            frag.appendChild(c);
          });
          row.appendChild(frag);
        }
        addSet();
        var firstCopy = row.querySelector('[data-qb-rv-copy]');
        setWidth = firstCopy.offsetLeft - originals[0].offsetLeft;
        /* enough copies that the row never runs out on a wide screen */
        var guard = 0;
        while (row.scrollWidth - setWidth < vp.clientWidth + setWidth && guard++ < 6) addSet();
        initVideos(row);
        row.querySelectorAll('[data-qb-rv-copy] [data-qb-rv-sound]').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
      }

      function running() {
        var touching = touchAt && Date.now() - touchAt < 10000;
        return onScreen && !reduceMotion && !hovered && !focused && !userPaused && !soundVideo && !touching && Date.now() > holdUntil;
      }

      function tick(t) {
        raf = onScreen ? requestAnimationFrame(tick) : null;
        var dt = last ? Math.min(64, t - last) : 0;
        last = t;
        if (!running() || !setWidth) return;
        if (Math.abs(vp.scrollLeft - pos) > 2) pos = vp.scrollLeft;
        pos += (speed * dt) / 1000;
        if (pos >= setWidth) pos -= setWidth;
        vp.scrollLeft = pos;
      }

      function hold(ms) {
        holdUntil = Date.now() + ms;
      }

      /* the visitor moved the row: follow, and wrap back into the first set once they stop */
      var settle = null;
      vp.addEventListener('scroll', function () {
        if (Math.abs(vp.scrollLeft - pos) <= 2) return;
        pos = vp.scrollLeft;
        hold(2500);
        clearTimeout(settle);
        settle = setTimeout(function () {
          if (setWidth && pos >= setWidth) {
            pos = pos % setWidth;
            vp.scrollLeft = pos;
          }
        }, 400);
      }, { passive: true });

      if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        vp.addEventListener('mouseenter', function () { hovered = true; });
        vp.addEventListener('mouseleave', function () { hovered = false; });
      }
      /* keyboard focus holds the row; the focus a tap or click leaves behind does not */
      wall.addEventListener('focusin', function (e) {
        var t = e.target;
        focused = !!(t && t.matches && (function () {
          try { return t.matches(':focus-visible'); } catch (err) { return true; }
        })());
      });
      wall.addEventListener('focusout', function () { focused = false; });
      /* a finger on the row holds it; lifting it (or the browser taking over) lets it drift again soon */
      var touchAt = 0;
      vp.addEventListener('touchstart', function () { touchAt = Date.now(); }, { passive: true });
      function touchDone() {
        touchAt = 0;
        hold(2500);
      }
      vp.addEventListener('touchend', touchDone, { passive: true });
      vp.addEventListener('touchcancel', touchDone, { passive: true });

      /* drag with the mouse */
      var drag = null;
      vp.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return;
        drag = { x: e.clientX, left: vp.scrollLeft, moved: false };
      });
      window.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (Math.abs(dx) > 4) {
          drag.moved = true;
          vp.classList.add('is-dragging');
        }
        if (drag.moved) vp.scrollLeft = drag.left - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!drag) return;
        drag = null;
        vp.classList.remove('is-dragging');
        hold(2500);
      });
      vp.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });

      function step(dir) {
        var by = Math.round(vp.clientWidth * 0.5);
        if (dir < 0 && vp.scrollLeft < by && setWidth) {
          pos = vp.scrollLeft + setWidth;
          vp.scrollLeft = pos;
        }
        hold(3500);
        vp.scrollBy({ left: dir * by, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      var prev = wall.querySelector('[data-qb-rv-prev]');
      var next = wall.querySelector('[data-qb-rv-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      if (pauseBtn) {
        pauseBtn.addEventListener('click', function () {
          userPaused = !userPaused;
          pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
          pauseBtn.setAttribute('aria-label', userPaused ? 'Play the moving reviews' : 'Pause the moving reviews');
        });
      }

      /* "play with sound": one video at a time; the row holds still while it plays aloud */
      row.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-qb-rv-sound]');
        if (!btn) return;
        var video = btn.closest('.qb-rv__card').querySelector('video');
        if (!video) return;
        var on = btn.getAttribute('aria-pressed') !== 'true';
        row.querySelectorAll('[data-qb-rv-sound]').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
          b.setAttribute('aria-label', 'Play this video with sound');
        });
        row.querySelectorAll('video').forEach(function (v) {
          v.muted = true;
        });
        soundVideo = null;
        if (on) {
          video.muted = false;
          video.qbUserPaused = false;
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
          btn.setAttribute('aria-pressed', 'true');
          btn.setAttribute('aria-label', 'Mute this video');
          soundVideo = video;
        }
      });

      if (hasIO) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            onScreen = entry.isIntersecting;
            if (onScreen && !raf) {
              last = 0;
              raf = requestAnimationFrame(tick);
            }
            if (!onScreen && soundVideo) {
              soundVideo.muted = true;
              row.querySelectorAll('[data-qb-rv-sound]').forEach(function (b) {
                b.setAttribute('aria-pressed', 'false');
              });
              soundVideo = null;
            }
          });
        }, { threshold: 0.1 }).observe(vp);
      }

      makeCopies();
      var resizeTimer = null;
      var lastWidth = window.innerWidth;
      window.addEventListener('resize', function () {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          speed = window.matchMedia('(max-width: 749px)').matches ? 26 : 34;
          makeCopies();
          pos = setWidth ? vp.scrollLeft % setWidth : 0;
          vp.scrollLeft = pos;
        }, 200);
      });
    });
  }

  /* Why parents choose QBounce, comparison card: QBounce's ticks pop in and the totals' meters
     fill the first time the card scrolls into view (a card already on screen at load is shown
     as is); on phones the pills pick which competitor stands next to QBounce. */
  function initVersus(scope) {
    (scope || document).querySelectorAll('[data-qb-vs]').forEach(function (wrap) {
      if (wrap.qbVs) return;
      wrap.qbVs = true;
      var pills = Array.prototype.slice.call(wrap.querySelectorAll('[data-vs-pick]'));
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          wrap.setAttribute('data-vs-show', pill.getAttribute('data-vs-pick'));
          pills.forEach(function (p) {
            p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
          });
        });
      });
      var card = wrap.querySelector('.qb-vs__card') || wrap;
      if (reduceMotion || !hasIO) return;
      var r = card.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9 && r.bottom > 0) return;
      wrap.setAttribute('data-qb-vs-ready', '');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          wrap.classList.add('is-in');
          io.disconnect();
        });
      }, { threshold: 0.35 });
      io.observe(card);
    });
  }

  /* Why parents choose QBounce, basketball scoreboard: as the card scrolls up the screen the rows
     are played one by one; each played row lights, its point goes on the board (bump and "+1"),
     the pips fill and the status shows the row being played ("Final" at the end). Scrolling back
     takes the points off again. The served page shows the final score, so nothing changes with
     reduced motion. Phones: the pills pick which competitor stands next to QBounce. */
  function initScoreboard(scope) {
    (scope || document).querySelectorAll('[data-qb-sb]').forEach(function (wrap) {
      if (wrap.qbSb) return;
      wrap.qbSb = true;
      var pills = Array.prototype.slice.call(wrap.querySelectorAll('[data-vs-pick]'));
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          wrap.setAttribute('data-vs-show', pill.getAttribute('data-vs-pick'));
          pills.forEach(function (p) {
            p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
          });
        });
      });
      if (reduceMotion) return;
      var rows = Array.prototype.slice.call(wrap.querySelectorAll('[data-sb-row]'));
      var n = rows.length;
      if (!n) return;
      var you = rows.map(function (r) { return r.getAttribute('data-you') === '1' ? 1 : 0; });
      var comps = rows.map(function (r) { return (r.getAttribute('data-c') || '').split('').map(Number); });
      var labels = rows.map(function (r) { var th = r.querySelector('th'); return th ? th.textContent.trim() : ''; });
      var pips = Array.prototype.slice.call(wrap.querySelectorAll('.qb-sb__pips i'));
      var youEl = wrap.querySelector('[data-sb-you]');
      var restEl = wrap.querySelector('[data-sb-rest]');
      var status = wrap.querySelector('[data-sb-status]');
      var shown = -1;
      var scores = { you: null, rest: null };
      var ticking = false;
      var skip = nearGate(wrap, onScroll);

      function bump(el) {
        var box = el && el.parentNode;
        if (!box) return;
        box.classList.remove('is-bump');
        void box.offsetWidth;
        box.classList.add('is-bump');
      }

      function show(k) {
        if (k === shown) return;
        var first = shown < 0;
        shown = k;
        var y = 0;
        var best = 0;
        var tally = [];
        rows.forEach(function (r, i) {
          var on = i < k;
          r.classList.toggle('is-on', on);
          r.classList.toggle('is-now', i === k - 1 && k < n + 1);
          if (pips[i]) pips[i].classList.toggle('is-on', on);
          if (!on) return;
          y += you[i];
          comps[i].forEach(function (v, c) {
            tally[c] = (tally[c] || 0) + v;
          });
        });
        tally.forEach(function (t) {
          if (t > best) best = t;
        });
        if (scores.you !== null && y > scores.you && !first) bump(youEl);
        if (scores.rest !== null && best > scores.rest && !first) bump(restEl);
        scores.you = y;
        scores.rest = best;
        if (youEl) setText(youEl, String(y));
        if (restEl) setText(restEl, String(best));
        if (status) setText(status, k === 0 ? 'Tip-off' : k >= n ? 'Final' : labels[k - 1]);
        wrap.classList.toggle('is-final', k >= n);
      }

      function update() {
        ticking = false;
        if (skip()) return;
        var vh = window.innerHeight;
        var r = wrap.getBoundingClientRect();
        /* starts as the card comes up into view; every row is played by the time the card's top is a
           quarter of the way down the screen (or its bottom is near the screen's bottom, if taller) */
        var start = vh * 0.8;
        var end = Math.min(vh * 0.25, vh * 0.92 - r.height);
        var p = Math.min(1, Math.max(0, (start - r.top) / Math.max(1, start - end)));
        show(Math.min(n, Math.floor(p * (n + 1))));
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    });
  }

  /* Grow Into Your Own Champion, "Shoppable clips" (block ZY). Clips play muted while on screen
     (initVideos); the sound button, or a tap on the clip, plays one clip aloud at a time and leaving
     the section mutes it again. The line under each clip follows its play position (only while the
     section is on screen). View counts count up once when their card comes in. Phones: dots follow
     the swipe. */
  function initClips(scope) {
    (scope || document).querySelectorAll('[data-qb-cl]').forEach(function (wrap) {
      if (wrap.qbCl) return;
      wrap.qbCl = true;
      var grid = wrap.querySelector('[data-qb-cl-grid]');
      var cards = Array.prototype.slice.call(wrap.querySelectorAll('.qb-cl__card'));
      var dots = Array.prototype.slice.call(wrap.querySelectorAll('.qb-cl__dots i'));
      var tracks = cards.map(function (card) {
        var v = card.querySelector('video');
        var bar = card.querySelector('.qb-cl__track i');
        return v && bar ? { v: v, bar: bar } : null;
      }).filter(Boolean);
      var loud = null;

      function mute() {
        if (!loud) return;
        loud.v.muted = true;
        /* with reduced motion clips stay still unless someone is listening */
        if (reduceMotion) loud.v.pause();
        loud.btn.setAttribute('aria-pressed', 'false');
        loud.btn.setAttribute('aria-label', 'Play this video with sound');
        loud.card.classList.remove('is-loud');
        loud = null;
      }

      function toggle(card) {
        var v = card.querySelector('video');
        var btn = card.querySelector('[data-qb-cl-sound]');
        if (!v || !btn) return;
        var was = loud && loud.v === v;
        mute();
        if (was) return;
        v.muted = false;
        v.qbUserPaused = false;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Mute this video');
        card.classList.add('is-loud');
        loud = { v: v, btn: btn, card: card };
      }

      wrap.addEventListener('click', function (e) {
        var hit = e.target.closest('[data-qb-cl-sound], .qb-cl__media');
        if (hit) toggle(hit.closest('.qb-cl__card'));
      });

      /* the line is a compositor animation as long as the clip, kept in step with it: started,
         paused and re-synced from the clip's own events (a few times a second), no per-frame script */
      tracks.forEach(function (t) {
        var v = t.v;
        var anim = null;
        function sync() {
          var d = v.duration;
          if (!d || !isFinite(d) || !t.bar.animate) return;
          if (!anim) {
            anim = t.bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: d * 1000, iterations: Infinity, easing: 'linear' });
          }
          var want = v.currentTime * 1000;
          var at = (anim.currentTime || 0) % (d * 1000);
          if (Math.abs(at - want) > 120) anim.currentTime = want;
          var running = !v.paused && !v.ended && v.readyState > 2;
          if (running && anim.playState !== 'running') anim.play();
          if (!running && anim.playState === 'running') anim.pause();
        }
        ['playing', 'pause', 'waiting', 'seeked', 'timeupdate', 'loadedmetadata'].forEach(function (ev) {
          v.addEventListener(ev, sync);
        });
      });

      if (hasIO) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) mute();
          });
        }, { threshold: 0.05 }).observe(wrap);
      }

      /* view counts: "3.5M" counts up from 0.0M as its card comes in */
      if (!reduceMotion && hasIO) {
        var countIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            countIO.unobserve(entry.target);
            var c = entry.target.qbCount;
            var start = null;
            function step(t) {
              if (start === null) start = t;
              var p = Math.min(1, (t - start) / 1100);
              var q = 1 - Math.pow(1 - p, 3);
              setText(c.el, c.pre + (c.to * q).toFixed(c.dec) + c.post);
              if (p < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
          });
        }, { threshold: 0.45 });
        cards.forEach(function (card) {
          var num = card.querySelector('[data-qb-count]');
          var m = num && num.textContent.trim().match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
          if (!m) return;
          card.qbCount = { el: num, pre: m[1], to: parseFloat(m[2]), dec: (m[2].split('.')[1] || '').length, post: m[3] };
          setText(num, m[1] + (0).toFixed(card.qbCount.dec) + m[3]);
          countIO.observe(card);
        });
      }

      if (!dots.length || !grid) return;
      var ticking = false;
      function update() {
        ticking = false;
        var left = grid.getBoundingClientRect().left;
        var best = 0;
        var bestDist = Infinity;
        cards.forEach(function (card, i) {
          var d = Math.abs(card.getBoundingClientRect().left - left);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        if (grid.scrollLeft > 0 && grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 2) best = cards.length - 1;
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === best);
        });
      }
      grid.addEventListener('scroll', function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* Product page (theme setting qb_pdp → html.qb-pdp), visual only. Comparison table: the ✔ / ✘ cells get a
     class so CSS draws an ember tick or a faint dot (the character stays in the cell for screen readers), and the
     rows drop in one after another when the table scrolls into view. Customer Statsheet: each ring fills to its
     own percentage (CSS, --qb-fill) while its number counts up from 0. Without IntersectionObserver or with
     reduced motion everything is shown at its end state at once. */
  function initPdp(scope) {
    if (!root.classList.contains('qb-pdp')) return;
    var doc = scope || document;

    function whenSeen(el, fn) {
      if (reduceMotion || !hasIO) {
        el.classList.add('qb-pdp-in');
        if (fn) fn(false);
        return;
      }
      el.classList.add('qb-pdp-wait');
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        el.classList.remove('qb-pdp-wait');
        el.classList.add('qb-pdp-in');
        if (fn) fn(true);
      }, { threshold: 0.3 });
      io.observe(el);
    }

    doc.querySelectorAll('.pricing-table:not(.qb-pct) table').forEach(function (table) {
      if (table.qbPdp) return;
      table.qbPdp = true;
      table.querySelectorAll('td').forEach(function (td) {
        var t = td.textContent.replace(/\s|️/g, '');
        if (/^[✔✓☑]$/.test(t)) td.classList.add('qb-yes');
        else if (/^[✘✗✕×❌]$/.test(t)) td.classList.add('qb-no');
      });
      table.querySelectorAll('tbody tr').forEach(function (tr, i) {
        tr.style.setProperty('--i', i);
      });
      whenSeen(table);
    });

    doc.querySelectorAll('.results__rows-container').forEach(function (rows) {
      if (rows.qbPdp) return;
      rows.qbPdp = true;
      var counts = [];
      rows.querySelectorAll('.results__row').forEach(function (row, i) {
        row.style.setProperty('--i', i);
        var p = row.querySelector('.results__percentage p');
        var m = p && p.textContent.trim().match(/^(\D*)(\d+)(.*)$/);
        if (m) counts.push({ el: p, pre: m[1], to: parseInt(m[2], 10), post: m[3], delay: i * 150 });
      });
      whenSeen(rows, function (animate) {
        if (!animate) return;
        counts.forEach(function (c) {
          var start = null;
          function step(t) {
            if (start === null) start = t + c.delay;
            var p = Math.max(0, Math.min(1, (t - start) / 1200));
            setText(c.el, c.pre + Math.round(c.to * (1 - Math.pow(1 - p, 3))) + c.post);
            if (p < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        });
      });
      if (rows.classList.contains('qb-pdp-wait')) {
        counts.forEach(function (c) {
          setText(c.el, c.pre + '0' + c.post);
        });
      }
    });

    /* "You may also like" is fetched by the theme only when it comes within 400px of the screen, so on a quick
       scroll the cards arrive while the footer is in view and push it down (layout shift 0.3 at 1440). Ask for
       the same recommendations once the page is idle after loading: they are in place long before anyone gets
       there. Same request, same products, only earlier. */
    doc.querySelectorAll('.qb-rp product-recommendations').forEach(function (el) {
      if (el.qbEarly) return;
      el.qbEarly = true;
      function go() {
        if (el.querySelector('.grid__item') || typeof el.loadRecommendations !== 'function') return;
        if (el.observer) el.observer.unobserve(el);
        el.loadRecommendations(el.dataset.productId);
      }
      function idle() {
        var defined = window.customElements ? customElements.whenDefined('product-recommendations') : Promise.resolve();
        defined.then(function () {
          if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 3000 });
          else setTimeout(go, 1500);
        });
      }
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    });

    if (root.classList.contains('qb-pdp-plus')) initPdpPlus(doc, whenSeen);
  }

  function countUp(el, to, fmt, delay) {
    if (reduceMotion) return setText(el, fmt(to));
    var start = null;
    function step(t) {
      if (start === null) start = t + (delay || 0);
      var p = Math.max(0, Math.min(1, (t - start) / 1300));
      setText(el, fmt(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* Product page, "Livelier sections" (qb_pdp_plus). Sale countdown: a digit tile drops in when its number changes
     (shot clock; only while on screen). Map banner: the player count in the heading counts up. "We are going VIRAL":
     the clips' own view counts added up (e.g. 3.5M + 2.4M + 4.7M) and counted up, with a link to the clips. */
  function initPdpPlus(doc, whenSeen) {
    doc.querySelectorAll('.countdown-timer span[id^="countdown-"]').forEach(function (span) {
      if (span.qbFlip || !window.MutationObserver) return;
      span.qbFlip = true;
      span.qbLast = span.textContent;
      new MutationObserver(function () {
        var v = span.textContent;
        if (v === span.qbLast) return;
        span.qbLast = v;
        if (reduceMotion || !span.animate) return;
        var r = span.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        span.animate([
          { transform: 'translate3d(0, -40%, 0) scaleY(0.7)', opacity: 0 },
          { transform: 'none', opacity: 1 }
        ], { duration: 380, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      }).observe(span, { childList: true, characterData: true, subtree: true });
    });

    doc.querySelectorAll('.banner__heading').forEach(function (h) {
      if (h.qbPdp) return;
      h.qbPdp = true;
      var walker = document.createTreeWalker(h, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var m = node.nodeValue.match(/\d{1,3}(?:,\d{3})+/);
        if (!m) continue;
        var after = node.splitText(m.index);
        after.splitText(m[0].length);
        var num = document.createElement('span');
        num.className = 'qb-num';
        num.textContent = m[0];
        after.parentNode.replaceChild(num, after);
        var to = parseInt(m[0].replace(/,/g, ''), 10);
        var fmt = function (v) { return Math.round(v).toLocaleString('en-US'); };
        whenSeen(h, function (animate) {
          if (animate) countUp(num, to, fmt);
        });
        if (h.classList.contains('qb-pdp-wait')) setText(num, '0');
        break;
      }
    });

    initJumbotron(doc);

    var viral = doc.querySelector('section.videos-slider-section');
    if (viral && !viral.qbPdp) {
      viral.qbPdp = true;
      var total = 0;
      var unit = '';
      var dec = 1;
      document.querySelectorAll('.qb-cl__card').forEach(function (card) {
        var c = card.qbCount;
        if (!c) {
          var b = card.querySelector('[data-qb-count]');
          var m2 = b && b.textContent.trim().match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
          if (m2) c = { to: parseFloat(m2[2]), post: m2[3] };
        }
        if (!c) return;
        if (unit && c.post !== unit) return;
        unit = c.post;
        total += c.to;
      });
      if (total > 0) {
        var clips = document.querySelector('.qb-cl');
        var sec = clips && clips.closest('.shopify-section');
        var box = document.createElement('p');
        box.className = 'qb-viral';
        box.innerHTML = '<span class="qb-viral__dot" aria-hidden="true"></span><b></b><span class="qb-viral__label">views on our clips</span>' +
          (sec && sec.id ? '<a class="qb-viral__link" href="#' + sec.id + '">Watch them</a>' : '');
        var big = box.querySelector('b');
        var fmtV = function (v) { return v.toFixed(dec) + unit; };
        setText(big, fmtV(total));
        viral.appendChild(box);
        whenSeen(box, function (animate) {
          if (animate) countUp(big, total, fmtV);
        });
        if (box.classList.contains('qb-pdp-wait')) setText(big, fmtV(0));
      }
    }
  }

  /* Product page arena jumbotron (scrolling-text section under qb_pdp_plus): the strip's sentences take turns every
     4.5 s while the board is on screen, each flipping in letter by letter like a scoreboard (words never break), the
     small ball bounces at each change, and the page's sale countdown is mirrored as "Ends in hh:mm:ss". */
  function initJumbotron(doc) {
    doc.querySelectorAll('[data-qb-jt]').forEach(function (jt) {
      if (jt.qbJt) return;
      jt.qbJt = true;
      var msgEl = jt.querySelector('[data-qb-jt-msg]');
      var ball = jt.querySelector('.qb-jt__ball');
      var msgs = Array.prototype.map.call(jt.querySelectorAll('.qb-jt__src li'), function (li) { return li.textContent.trim(); }).filter(Boolean);
      var clock = jt.querySelector('[data-qb-jt-clock]');
      var timeEl = jt.querySelector('[data-qb-jt-time]');
      var h = document.getElementById('countdown-hours');
      var m = document.getElementById('countdown-minutes');
      var sec = document.getElementById('countdown-seconds');
      var onScreen = false;
      var idx = 0;
      var timer = null;

      function tick() {
        if (!h || !m || !sec) return;
        clock.hidden = false;
        setText(timeEl, h.textContent.trim() + ':' + m.textContent.trim() + ':' + sec.textContent.trim());
      }

      function write(text, animate) {
        msgEl.setAttribute('aria-label', text);
        msgEl.textContent = '';
        var n = 0;
        text.split(' ').forEach(function (word, wi) {
          if (wi) msgEl.appendChild(document.createTextNode(' '));
          var w = document.createElement('span');
          w.className = 'qb-jt__w';
          w.setAttribute('aria-hidden', 'true');
          word.split('').forEach(function (ch) {
            var l = document.createElement('span');
            l.className = 'qb-jt__l';
            l.textContent = ch;
            w.appendChild(l);
            if (animate && l.animate) {
              l.animate([
                { transform: 'perspective(400px) rotateX(-95deg)', opacity: 0 },
                { transform: 'perspective(400px) rotateX(12deg)', opacity: 1, offset: 0.7 },
                { transform: 'none', opacity: 1 }
              ], { duration: 520, delay: n * 22, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' });
            }
            n++;
          });
          msgEl.appendChild(w);
        });
        if (animate && ball) {
          ball.classList.remove('is-bounce');
          void ball.offsetWidth;
          ball.classList.add('is-bounce');
        }
      }

      function next() {
        if (!onScreen || document.hidden || msgs.length < 2) return;
        idx = (idx + 1) % msgs.length;
        if (msgEl.animate) {
          msgEl.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-in' }).onfinish = function () {
            write(msgs[idx], true);
          };
        } else {
          write(msgs[idx], false);
        }
      }

      write(msgs[0] || msgEl.textContent.trim(), false);
      tick();
      if (reduceMotion || !hasIO) {
        if (h) setInterval(tick, 1000);
        return;
      }
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        clearInterval(timer);
        if (onScreen) {
          write(msgs[idx], true);
          timer = setInterval(function () {
            tick();
            if (Date.now() - (jt.qbLast || 0) >= 4500) {
              jt.qbLast = Date.now();
              next();
            }
          }, 1000);
          jt.qbLast = Date.now();
        }
      }, { threshold: 0.4 }).observe(jt);
    });
  }

  /* Product page add-to-cart bar (qb_pdp_sticky, snippets/qb-pdp-sticky.liquid). Shown once the page's own Add to
     cart button is above the screen, hidden near the footer. It mirrors the chosen Kaching bundle (title, price, old
     price) and its button presses the page's own Add to cart, so bundles, upsells and the cart drawer are unchanged. */
  function initPdpSticky() {
    var bar = document.querySelector('[data-qb-ps]');
    if (!bar || bar.qbPs) return;
    bar.qbPs = true;
    var formId = bar.getAttribute('data-form') || '';
    var form = document.getElementById(formId);
    var real = document.getElementById('ProductSubmitButton-' + formId.replace(/^product-form-/, '')) ||
      (form && form.querySelector('[type="submit"]'));
    if (!real) return;
    var btn = bar.querySelector('[data-qb-ps-btn]');
    var titleEl = bar.querySelector('[data-qb-ps-title]');
    var priceEl = bar.querySelector('[data-qb-ps-price]');
    var wasEl = bar.querySelector('[data-qb-ps-was]');
    var baseTitle = titleEl.textContent;
    var info = document.querySelector('.product__info-container');
    var footer = document.querySelector('.footer, footer');
    var state = { realIn: true, realAbove: false, footIn: false };

    btn.addEventListener('click', function () {
      if (!real.disabled) real.click();
    });

    var queued = false;
    function mirror() {
      queued = false;
      var sel = info && info.querySelector('.kaching-bundles__bar--selected');
      if (sel) {
        var t = sel.querySelector('.kaching-bundles__bar-title');
        var p = sel.querySelector('.kaching-bundles__bar-price');
        var w = sel.querySelector('.kaching-bundles__bar-full-price');
        setText(titleEl, t ? t.textContent.trim() : baseTitle);
        if (p) setText(priceEl, p.textContent.trim());
        setText(wasEl, w ? w.textContent.trim() : '');
      }
      btn.disabled = !!real.disabled;
    }
    if (info && window.MutationObserver) {
      new MutationObserver(function () {
        if (!queued) {
          queued = true;
          requestAnimationFrame(mirror);
        }
      }).observe(info, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'disabled'] });
    }
    mirror();

    function update() {
      var show = !state.realIn && state.realAbove && !state.footIn;
      if (bar.classList.contains('is-shown') === show) return;
      bar.classList.toggle('is-shown', show);
      root.classList.toggle('qb-klp-ps', show); /* the sign-up tab steps up above the bar (qb-popup.css) */
      if (show) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
    }
    /* measured on scroll (one read per frame), not with IntersectionObserver: a jump (anchor link, restored scroll)
       never crosses the button's edge, so an observer would not report it */
    var ticking = false;
    function measure() {
      ticking = false;
      var vh = window.innerHeight;
      var r = real.getBoundingClientRect();
      state.realIn = r.bottom > 0 && r.top < vh && r.height > 0;
      state.realAbove = r.bottom <= 0;
      state.footIn = footer ? footer.getBoundingClientRect().top < vh : false;
      update();
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    measure();

    /* QBounce speed (qb_fast_has): html.qb-ps-away while the quick view or the cart drawer is open (the bar steps out of
       the way), html.qb-ps-pbar while Shopify's preview strip is in the page (the bar steps up). These were :has() rules
       on <html>; Shopify's checkout prefetch adds ~40 tags a second, and each one made the browser re-check them. A modal
       sets <body>'s class and its own open attribute together; the drawer adds .active a moment after, so each drawer is
       watched too (also one added to <body> later); the preview strip is added to <body> in one piece. */
    if (!bar.hasAttribute('data-qb-ps-cls') || !window.MutationObserver) return;
    function syncAway() {
      document.querySelectorAll('cart-drawer, .drawer').forEach(function (d) {
        if (d.qbPsAway) return;
        d.qbPsAway = true;
        awayMO.observe(d, { attributes: true, attributeFilter: ['class'] });
      });
      var away = !!document.querySelector('quick-add-modal[open], cart-drawer.active, .drawer.active');
      var strip = !!document.getElementById('PBarNextFrame');
      if (root.classList.contains('qb-ps-away') !== away) root.classList.toggle('qb-ps-away', away);
      if (root.classList.contains('qb-ps-pbar') !== strip) root.classList.toggle('qb-ps-pbar', strip);
    }
    var awayMO = new MutationObserver(syncAway);
    awayMO.observe(document.body, { childList: true, attributes: true, attributeFilter: ['class'] });
    syncAway();
  }

  function init(scope) {
    decorateHero(scope);
    initTilt(scope);
    initAnnBar(scope);
    initEmbers(scope);
    initOffscreenPause(scope);
    initRings(scope);
    initReviewWall(scope);
    initVideos(scope);
    initHeroVideoToggle(scope);
    initFooterFold(scope);
    initMarquee(scope);
    initRoom(scope);
    initKit(scope);
    initStepsTimeline(scope);
    initDeck(scope);
    initResults(scope);
    initDayStory(scope);
    initVersus(scope);
    initScoreboard(scope);
    initClips(scope);
    initPdp(scope);
    initReveal(scope);
  }

  root.classList.add('qb-js');

  /* Sign-up popup (Klaviyo "Mystery Deal", assets/qb-popup.css, Theme settings > QBounce popup = html.qb-klp).
     - While it is open the page behind it rests: html.qb-klp-open pauses the page's CSS loops (qb-popup.css) and the
       videos playing at that moment are paused here, then played again on close (those still near the screen).
       Klaviyo tells when it opens and closes (klaviyoForms events); a check every 1.5 s while open covers a popup
       that goes without a close event.
     - The "Mystery Deal!" tab steps up over what shares its corner: Shopify's preview bar (qb-klp-pbar), another
       app's "Someone from … purchased" note while it shows (qb-klp-sales); the product page's sticky bar sets
       qb-klp-ps in initPdpSticky. These were :has() rules on <html>, which made every element added or removed
       anywhere restyle the whole page. */
  function initSignupPopup() {
    if (!root.classList.contains('qb-klp') || !document.body) return;
    var held = [];
    var check = null;
    function popup() {
      return document.querySelector('[data-testid="POPUP"]');
    }
    function rest(on) {
      if (root.classList.contains('qb-klp-open') === on) return;
      root.classList.toggle('qb-klp-open', on);
      if (on) {
        document.querySelectorAll('#MainContent video, .shopify-section video').forEach(function (v) {
          if (v.paused) return;
          v.pause();
          held.push(v);
        });
        check = setInterval(function () {
          if (!popup()) rest(false);
        }, 1500);
        return;
      }
      clearInterval(check);
      held.forEach(function (v) {
        if (!v.isConnected || v.qbUserPaused) return;
        var r = v.getBoundingClientRect();
        if (r.bottom < -200 || r.top > window.innerHeight + 200) return;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
      held = [];
    }
    window.addEventListener('klaviyoForms', function (e) {
      var type = e.detail && e.detail.type;
      if (type === 'close') return rest(false);
      if (type !== 'open') return;
      /* 'open' is sent for flyouts too: rest only for the popup (drawn a moment after the event) */
      var tries = 0;
      (function look() {
        if (popup()) rest(true);
        else if (++tries < 10) setTimeout(look, 100);
      })();
    });
    if (popup()) rest(true);

    var note = null;
    var noteMO = window.MutationObserver ? new MutationObserver(syncNote) : null;
    function syncNote() {
      root.classList.toggle('qb-klp-sales', !!(note && note.isConnected && note.classList.contains('bundle-fade-in')));
    }
    function scan() {
      root.classList.toggle('qb-klp-pbar', !!document.getElementById('PBarNextFrameWrapper'));
      var el = document.getElementById('bundle-recent_sales');
      if (el === note || !noteMO) return;
      note = el;
      noteMO.disconnect();
      if (el) noteMO.observe(el, { attributes: true, attributeFilter: ['class'] });
      syncNote();
    }
    scan();
    /* both are added to <body> by other scripts, at any time */
    if (window.MutationObserver) new MutationObserver(scan).observe(document.body, { childList: true });
  }
  initSignupPopup();

  /* Phone and tablet menu (Theme settings > Fix: phone and tablet menu = html.qb-fix-menu; qb-revamp-sections.css ZZV).
     - The theme locks the page while the menu is open by adding overflow-hidden-tablet/-mobile to <body>; the same
       class on <html> (qb-lock-tablet/-mobile) moves that lock to <html>, so the sticky header stays where it is.
     - A row tapped while the rows still drop in finishes its drop-in at once: until it ends the row is the box its
       submenu is sized against. */
  function initMenuFix() {
    if (!root.classList.contains('qb-fix-menu') || !document.body) return;
    var body = document.body;
    function sync() {
      ['tablet', 'mobile'].forEach(function (k) {
        var on = body.classList.contains('overflow-hidden-' + k);
        if (root.classList.contains('qb-lock-' + k) !== on) root.classList.toggle('qb-lock-' + k, on);
      });
    }
    sync();
    if (window.MutationObserver) new MutationObserver(sync).observe(body, { attributes: true, attributeFilter: ['class'] });
    var drawer = document.getElementById('menu-drawer');
    if (!drawer) return;
    drawer.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('summary')) return;
      drawer.querySelectorAll('.menu-drawer__navigation > .menu-drawer__menu > li').forEach(function (li) {
        if (li.getAnimations) li.getAnimations().forEach(function (a) { a.finish(); });
      });
    });
  }
  ready(initMenuFix);

  /* QBounce speed (qb_fast_css): on the homepage the full stylesheet arrives after the first paint
     (snippets/qb-revamp-head.liquid sets html.qb-css-wait until it is applied, then fires qb:css).
     Start once it is there, so sections are set up and measured with their real styles; give up
     waiting after 10s. */
  function readyStyled(fn) {
    ready(function () {
      if (!root.classList.contains('qb-css-wait')) return fn();
      var done = false;
      function go() {
        if (done) return;
        done = true;
        fn();
      }
      document.addEventListener('qb:css', go, { once: true });
      setTimeout(go, 10000);
    });
  }

  readyStyled(function () {
    if (root.classList.contains('qb-pdp')) initPdpSticky();
    initProgress();
    initSpotlight();
    init(document);
    initRise();
    initMoon();
    initFlow();
    initJoin();
  });

  /* Theme editor: selecting an announcement shows it and holds it while it is being edited */
  document.addEventListener('shopify:block:select', function (e) {
    var bar = e.target.closest && e.target.closest('[data-qb-annbar]');
    if (!bar || !bar.qbAnnBar) return;
    bar.qbAnnBar.pause(true);
    bar.qbAnnBar.show(bar.qbAnnBar.msgs.indexOf(e.target));
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var bar = e.target.closest && e.target.closest('[data-qb-annbar]');
    if (bar && bar.qbAnnBar) bar.qbAnnBar.pause(false);
  });

  /* Theme editor: re-run when a section is added or re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    e.target.querySelectorAll('.qb-reveal').forEach(function (el) {
      el.classList.add('qb-in');
    });
  });
})();
